import { PaymentFormData } from "@/Components/UI/Onboarding/Client/PaymentForm/PaymentForm";
import { buildFaqItems } from "@/Components/UI/PaymentFaq/faqItems";
import { AnalyticsContext } from "@/Contexts/AnalyticsContext";
import { useContext, useEffect, useRef, useState } from "react";
import { IoChevronDown } from "react-icons/io5";

export default function ProactivePaymentFaq({
    paymentFormData,
    forceShow = false,
    includeCoachingEvidence = false,
}: {
    paymentFormData: PaymentFormData;
    forceShow?: boolean;
    includeCoachingEvidence?: boolean;
}) {
    const { trackEvent, triggerExposureOnBackend } =
        useContext(AnalyticsContext);
    const [openSlug, setOpenSlug] = useState<string | null>(null);
    const hasExposedCoachingEvidence = useRef(false);

    const isShown =
        forceShow ||
        paymentFormData.faqsOnProactivePaymentPageVariant === "treatment";

    /**
     * Only clients who actually see this list get a variant-dependent
     * experience, so exposure is recorded here rather than by the surface that
     * mounts this component — that surface cannot know whether the list will
     * render. Both arms are exposed, since the list renders either way.
     */
    useEffect(() => {
        if (
            !isShown ||
            !includeCoachingEvidence ||
            hasExposedCoachingEvidence.current
        ) {
            return;
        }
        hasExposedCoachingEvidence.current = true;
        void triggerExposureOnBackend("coaching_evidence_faq");
    }, [isShown, includeCoachingEvidence]);

    if (!isShown) {
        return null;
    }

    const items = buildFaqItems(paymentFormData, { includeCoachingEvidence });

    const onToggle = (slug: string) => {
        const isOpening = openSlug !== slug;
        setOpenSlug(isOpening ? slug : null);
        if (isOpening) {
            trackEvent("faq_expanded", {
                page: route().current() ?? "unknown",
                faq: slug,
            });
        }
    };

    return (
        <div className="flex flex-col gap-2 pb-6">
            <h2 className="mt-4 px-6 text-xs font-semibold uppercase tracking-wider text-brand-gray">
                A few things to know
            </h2>
            <ul className="flex flex-col border-y border-brand-border bg-brand-white">
                {items.map((item, index) => {
                    const isOpen = openSlug === item.slug;
                    const panelId = `proactive-faq-panel-${item.slug}`;
                    return (
                        <li
                            key={item.slug}
                            className={
                                index > 0
                                    ? "border-t border-brand-border"
                                    : undefined
                            }
                        >
                            <button
                                type="button"
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                                onClick={() => onToggle(item.slug)}
                                className="flex w-full items-center gap-3 px-6 py-3.5 text-left transition-colors hover:bg-brand-lightGray/60"
                            >
                                <span
                                    aria-hidden="true"
                                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-lightRed text-base text-brand-red"
                                >
                                    {item.icon}
                                </span>
                                <span className="grow text-sm font-medium text-brand-black sm:text-base">
                                    {item.title}
                                </span>
                                <IoChevronDown
                                    aria-hidden="true"
                                    className={`shrink-0 text-brand-gray transition-transform duration-200 ${
                                        isOpen ? "rotate-180" : ""
                                    }`}
                                />
                            </button>
                            <div
                                id={panelId}
                                role="region"
                                aria-hidden={!isOpen}
                                className={`grid overflow-hidden transition-[grid-template-rows] duration-200 ease-out ${
                                    isOpen
                                        ? "grid-rows-[1fr]"
                                        : "grid-rows-[0fr]"
                                }`}
                            >
                                <div className="min-h-0 overflow-hidden">
                                    <div className="flex flex-col gap-3 px-6 pb-4 text-sm leading-relaxed text-brand-gray">
                                        {item.body}
                                    </div>
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
